import { useMutation } from '@apollo/client';
import { produce } from 'immer';
import debounce from 'lodash/debounce';
import { useCallback, useMemo, useState } from 'react';
import { v4 } from 'uuid';

import { cacheUtils, useCompleteFragment } from '@eluve/apollo-client';
import { CodeType } from '@eluve/blocks';
import { useToast } from '@eluve/components';
import { useTranslation } from '@eluve/i18n';

import {
  addAppointmentBillingCodeLinksMutation,
  removeAppointmentBillingCodeLinksMutation,
  updateAppointmentBillingCodeModifiersMutation,
  updateAppointmentBillingCodePriceMutation,
  updateAppointmentBillingCodeQuantityMutation,
} from './AppointmentBillingCodePrices.operations';
import { appointmentBillingCodesFragment } from './AppointmentBillingCodes.operations';

// Types

export type AppointmentBillingCode = {
  id: string;
  medical_code: AppointmentMedicalCode | null;
  price: number | null;
  quantity: number | null;
};

export type AppointmentMedicalCode = {
  id: string;
  code: string;
  codeType: CodeType;
  description: string | null;
};

// Hooks

export const useAppointmentBillingCodes = ({
  tenantId,
  appointmentId,
}: {
  tenantId: string;
  appointmentId: string;
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const data = useCompleteFragment({
    fragment: appointmentBillingCodesFragment,
    key: { id: appointmentId },
    strict: false,
  });
  const billingCodes = data?.billingCodes ?? [];

  // Modifiers

  const [updateAppointmentBillingCodeModifiers] = useMutation(
    updateAppointmentBillingCodeModifiersMutation,
  );

  const updateModifiers = (
    billingCodeId: string,
    modifiers: string[] | null,
  ) => {
    updateAppointmentBillingCodeModifiers({
      variables: {
        billingCodeId,
        modifiers,
      },
      onError: () =>
        toast.error(t('appointmentTools.billing.updateModifiersFailed')),
      optimisticResponse: ({ billingCodeId, modifiers }) => {
        return {
          updateAppointmentBillingCodesByPk: {
            __typename: 'AppointmentBillingCodes' as const,
            id: billingCodeId,
            modifiers: modifiers ?? null,
          },
        };
      },
    });
  };

  // Price

  const [updateAppointmentBillingCodePrice] = useMutation(
    updateAppointmentBillingCodePriceMutation,
  );

  const updatePrice = (billingCodeId: string, price: number) => {
    updateAppointmentBillingCodePrice({
      variables: {
        billingCodeId,
        price,
      },
      onError: () =>
        toast.error(t('appointmentTools.billing.updatePriceFailed')),
      optimisticResponse: ({ billingCodeId, price }) => {
        return {
          updateAppointmentBillingCodesByPk: {
            __typename: 'AppointmentBillingCodes' as const,
            id: billingCodeId,
            price,
          },
        };
      },
    });
  };

  // Quantity

  const [codeQuantities, setCodeQuantities] = useState<
    { cptId: string; quantity: number }[]
  >([]);

  const getQuantity = (billingCode: AppointmentBillingCode) => {
    return (
      codeQuantities.find((q) => q.cptId === billingCode.id)?.quantity ??
      billingCode.quantity ??
      1
    );
  };

  const [updateAppointmentBillingCodeQuantity] = useMutation(
    updateAppointmentBillingCodeQuantityMutation,
  );

  const _updateQuantity = useCallback(
    (billingCodeId: string, quantity: number) => {
      updateAppointmentBillingCodeQuantity({
        variables: {
          billingCodeId,
          quantity,
        },
        onError: () => {
          toast.error(t('appointmentTools.billing.updateQuantityFailed'));

          setCodeQuantities((prev) =>
            prev.filter((quantity) => quantity.cptId !== billingCodeId),
          );
        },
        optimisticResponse: ({ billingCodeId, quantity }) => {
          return {
            updateAppointmentBillingCodesByPk: {
              __typename: 'AppointmentBillingCodes' as const,
              id: billingCodeId,
              quantity,
            },
          };
        },
      });
    },
    [updateAppointmentBillingCodeQuantity, toast, setCodeQuantities, t],
  );

  const updateQuantityDebounced = useMemo(
    () => debounce(_updateQuantity, 250),
    [_updateQuantity],
  );

  const updateQuantity = (cptId: string, quantity: number) => {
    updateQuantityDebounced(cptId, quantity);
    setCodeQuantities((prev) => {
      const index = prev.findIndex((q) => q.cptId === cptId);
      if (index === -1) {
        return [...prev, { cptId, quantity }];
      }
      return [
        ...prev.slice(0, index),
        { cptId, quantity },
        ...prev.slice(index + 1),
      ];
    });
  };

  // Linking

  const [addAppointmentBillingCodeLinks] = useMutation(
    addAppointmentBillingCodeLinksMutation,
    {
      onError: () => {
        toast.error(t('appointmentTools.billing.selectIcdFailed'));
      },
    },
  );

  const [removeAppointmentBillingCodeLinks] = useMutation(
    removeAppointmentBillingCodeLinksMutation,
    {
      onError: () => {
        toast.error(t('appointmentTools.billing.deselectIcdFailed'));
      },
    },
  );

  const addBillingCodeLinks = (cptId: string, icdIds: string[]) => {
    addAppointmentBillingCodeLinks({
      variables: {
        billingCodeLinks: icdIds.map((icdId) => ({
          appointmentId,
          sourceAppointmentBillingCodeId: cptId,
          targetAppointmentBillingCodeId: icdId,
        })),
      },
      optimisticResponse: () => ({
        insertAppointmentBillingCodeLinks: {
          returning: icdIds.map((icdId) => ({
            __typename: 'AppointmentBillingCodeLinks' as const,
            id: v4(),
            sourceAppointmentBillingCodeId: cptId,
            targetAppointmentBillingCodeId: icdId,
          })),
        },
      }),
      update: (_cache, { data }) => {
        cacheUtils.updateFragment(
          {
            fragment: appointmentBillingCodesFragment,
            key: {
              id: appointmentId,
            },
          },
          (existing) => {
            const newLinks = data?.insertAppointmentBillingCodeLinks?.returning;
            if (!existing || !newLinks) {
              return existing;
            }

            return produce(existing, (draft) => {
              const billingCode = draft.billingCodes.find(
                (code) => code.id === cptId,
              );
              billingCode?.linked_codes.push(...newLinks);
            });
          },
        );
      },
    });
  };

  const removeBillingCodeLinks = (cptId: string, icdIds: string[]) => {
    removeAppointmentBillingCodeLinks({
      variables: {
        tenantId,
        appointmentId,
        sourceBillingCodeId: cptId,
        targetBillingCodeIds: icdIds,
      },
      optimisticResponse: () => ({
        deleteAppointmentBillingCodeLinks: {
          returning: icdIds.map((icdId) => ({
            __typename: 'AppointmentBillingCodeLinks' as const,
            id: v4(),
            sourceAppointmentBillingCodeId: cptId,
            targetAppointmentBillingCodeId: icdId,
          })),
        },
      }),
      update: (_cache) => {
        cacheUtils.updateFragment(
          {
            fragment: appointmentBillingCodesFragment,
            key: {
              id: appointmentId,
            },
          },
          (existing) => {
            if (!existing) {
              return existing;
            }

            return produce(existing, (draft) => {
              const billingCode = draft.billingCodes.find(
                (code) => code.id === cptId,
              );
              if (!billingCode?.linked_codes) {
                return;
              }
              billingCode.linked_codes = billingCode.linked_codes.filter(
                (link) => !icdIds.includes(link.targetAppointmentBillingCodeId),
              );
            });
          },
        );
      },
    });
  };

  const updateLink = (isSelected: boolean, cptId: string, icdId: string) => {
    if (isSelected) {
      removeBillingCodeLinks(cptId, [icdId]);
    } else {
      addBillingCodeLinks(cptId, [icdId]);
    }
  };

  const updateLinks = (
    cptId: string,
    linkedIcdIds: string[],
    icdIds: string[],
  ) => {
    const isComplete = linkedIcdIds.length === icdIds.length;
    if (isComplete) {
      removeBillingCodeLinks(cptId, linkedIcdIds);
    } else {
      addBillingCodeLinks(
        cptId,
        icdIds.filter((icdId) => !linkedIcdIds.includes(icdId)),
      );
    }
  };

  return {
    billingCodes,
    getQuantity,
    updateLink,
    updateLinks,
    updateModifiers,
    updatePrice,
    updateQuantity,
  };
};
